/**
 * Wave K — file format IO, audit/repair tools, data tables, parameters,
 * xref operations and automation commands. Import parsing reuses the
 * CadParserService (DXF/DWG) pattern: formats that cannot be decoded
 * locally report honestly instead of failing silently; export/audit/
 * parametrize operate on real ledger data. (docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PT, PN, PTxt, PK, PLoopK, requireSelection, entityToPolyline, makeEntity } from './lib';
import { undoableAdd, undoableDelete, undoableModify, undoableReplace } from '../entityOps';
import { useAppStore, type Entity } from '../../../state/store';
import { useDocumentStore } from '../../../state/useDocumentStore';
import { useBIMStore } from '../../../state/useBIMStore';
import { useMacroStore, useViewStore, useBlockStore } from '../../../state/commandAuxStores';
import { parseDocument } from '../../../utils/ProjectFileIO';
import { isFileSystemAccessSupported, pickFileToOpen, readHandleText } from '../../../services/fileSystem/fileAccess';
import { downloadTextFile } from '../../../services/fileSystem/fileAccess';
import { globalParameterManager } from '../../../core/services';
import { notify } from '../../../utils/notify';
import { parseCsv, normalizeTableCells, useDataLinkStore } from '../tableModel';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import i18n from '../../../i18n';
import { useAcadPgpStore } from '../../../state/useAcadPgpStore';
import { reapplyAcadPgp } from '../../../utils/acadPgp';

/** Open a file picker (native or in-app) and hand text to a callback. */
async function pickAndRead(label: string, onText: (name: string, text: string) => void): Promise<void> {
    if (isFileSystemAccessSupported()) {
        const handle = await pickFileToOpen();
        if (!handle) return;
        const text = await readHandleText(handle);
        onText(handle.name, text);
    }
    // Fallback: in-app browser (existing flow) — guide the user.
    window.dispatchEvent(new CustomEvent('yap:open-file-browser', { detail: { mode: 'open' } }));
}

export const fileRunners: RunnerMap = {
    'file.dgnImport': () => importFormat('DGNIMPORT', 'dgn'),
    'file.dgnAttach': () => importFormat('DGNATTACH', 'dgn'),
    'file.fbxImport': () => importFormat('FBXIMPORT', 'fbx'),
    'file.markup': () => importFormat('MARKUP', 'dwf'),

    'file.recover': () => {
        const doc = useDocumentStore.getState();
        const objs = Object.values(doc.sceneObjects);
        const bad = objs.filter((o) => !o.id || !o.source);
        if (bad.length === 0 && objs.length > 0) {
            return `RECOVER: document structure is healthy (${objs.length} objects).`;
        }
        if (objs.length === 0) return 'RECOVER: the current document is empty.';
        const cleaned = objs.filter((o) => o.id && o.source);
        doc.replaceSceneLedger(cleaned);
        return `RECOVER: ${bad.length} malformed record(s) dropped; ${cleaned.length} kept.`;
    },

    'file.audit': () => {
        const { entities } = useAppStore.getState();
        const ids = Object.keys(entities);
        const orphans = ids.filter((id) => !useDocumentStore.getState().sceneObjects[id]);
        const selfRefs = ids.filter((id) => {
            const e = entities[id];
            return e.type === 'line' && JSON.stringify(e.data.start) === JSON.stringify(e.data.end);
        });
        const issues = orphans.length + selfRefs.length;
        if (issues === 0) return `AUDIT: no errors found (${ids.length} entities checked).`;
        notify(i18n.t('tools.notify.auditResult', { orphans: orphans.length, selfRefs: selfRefs.length }), 'warning');
        return `AUDIT: ${issues} issue(s) reported (see messages).`;
    },

    'file.overkill': () => {
        const { entities, select, updateEntity, deleteEntity } = useAppStore.getState();
        const tol = 1e-4;
        const keys = Object.keys(entities);
        let removed = 0;
        for (let i = 0; i < keys.length; i++) {
            const a = entities[keys[i]];
            if (!a || a.type !== 'line') continue;
            for (let j = i + 1; j < keys.length; j++) {
                const b = entities[keys[j]];
                if (!b || b.type !== 'line') continue;
                const s1 = a.data.start as number[], e1 = a.data.end as number[];
                const s2 = b.data.start as number[], e2 = b.data.end as number[];
                if (!s1 || !e1 || !s2 || !e2) continue;
                const same = (Math.hypot(s2[0] - s1[0], s2[1] - s1[1], (s2[2] ?? 0) - (s1[2] ?? 0)) < tol
                    && Math.hypot(e2[0] - e1[0], e2[1] - e1[1], (e2[2] ?? 0) - (e1[2] ?? 0)) < tol)
                    || (Math.hypot(s2[0] - e1[0], s2[1] - e1[1], (s2[2] ?? 0) - (e1[2] ?? 0)) < tol
                        && Math.hypot(e2[0] - s1[0], e2[1] - s1[1], (e2[2] ?? 0) - (s1[2] ?? 0)) < tol);
                if (same) { deleteEntity(b.id); removed++; }
            }
        }
        select([]);
        void updateEntity;
        return removed > 0 ? `OVERKILL: ${removed} duplicate line(s) removed.` : 'OVERKILL: no duplicates found.';
    },

    // ---------------- external reference operations (block-backed) ----------------
    // In this product the reference citizens are block INSERTs (the DWG
    // link itself lives in the BIM store). BIND freezes an insert against
    // definition changes by snapshotting the def into a private anonymous
    // block owned by that instance; XCLIP stores a real clip polygon the
    // BlockRefMesh renderer consumes; REFEDIT expands the reference for
    // in-place editing and BLOCK <name> redefines from the working set.
    'xref.bind': () => {
        runInteraction({
            steps: [PK('BIND: pick a block reference', ['insert'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'insert') return 'BIND: pick a block reference.';
                const name = String(e.data.blockName ?? '');
                const def = useBlockStore.getState().defs[name];
                if (!def) return 'BIND: block definition missing.';
                const boundName = `${name}$bound$${Date.now().toString(36)}`;
                useBlockStore.getState().define({
                    name: boundName,
                    entities: structuredClone(def.entities),
                    basePoint: [0, 0, 0],
                    attributes: structuredClone(def.attributes ?? []),
                });
                undoableModify([picks[0]], (d) => ({ ...d, blockName: boundName, bound: true }));
                return `BIND: "${name}" embedded as "${boundName}" — this instance no longer tracks the original definition.`;
            },
        });
        return 'BIND: pick a block reference';
    },
    'xref.clip': () => {
        runInteraction({
            steps: [PK('XCLIP: pick a reference (block/image)'), PLoopK('XCLIP: boundary corners (3+)', 3)],
            complete: (pts, _v, picks) => {
                const poly: Pt3[] = pts.map((p) => [p.x, p.y, p.z] as Pt3);
                undoableModify([picks[0]], (d) => ({ ...d, clipBoundary: poly }));
                return 'XCLIP: clip boundary applied (contents outside the polygon hide).';
            },
        });
        return 'XCLIP: pick the reference';
    },
    'xref.edit': () => {
        const { selectedIds, entities } = useAppStore.getState();
        const id = selectedIds.find((i) => entities[i]?.type === 'insert');
        if (!id) return 'REFEDIT: select a block reference first.';
        const e = entities[id];
        const name = String(e.data.blockName ?? '');
        const def = useBlockStore.getState().defs[name];
        if (!def) return 'REFEDIT: block definition missing.';
        // Expand the reference into editable components tagged with its
        // source; BLOCK <name> afterwards redefines the block from a
        // selection that carries this working set (in-place cycle).
        const pos = (e.data.position as number[]) ?? [0, 0, 0];
        const scale = typeof e.data.scale === 'number' && e.data.scale > 0 ? e.data.scale : 1;
        const rot = ((e.data.rotation as number) ?? 0) * Math.PI / 180;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const mapPt = (pt: number[]): number[] => {
            const x = (pt[0] ?? 0) * scale, y = (pt[1] ?? 0) * scale;
            return [(pos[0] ?? 0) + x * cos - y * sin, (pos[1] ?? 0) + x * sin + y * cos, (pos[2] ?? 0) + (pt[2] ?? 0) * scale];
        };
        const working: Entity[] = def.entities.map((src) => {
            const data = structuredClone(src.data) as Record<string, any>;
            ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = mapPt(data[k]); });
            ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as number[][]).map(mapPt); });
            return { ...src, id: `cad:${crypto.randomUUID()}`, data: { ...data, refEditSource: name } };
        });
        undoableReplace([id], working);
        useAppStore.getState().select(working.map((w) => w.id));
        notify(i18n.t('tools.notify.refeditExpanded', { name: name, blockName: name }), 'info');
        return 'REFEDIT: reference expanded for in-place editing.';
    },
    'xref.pointCloudCrop': () => {
        const bim = useBIMStore.getState();
        const cloudId = bim.selection.find((id) => (bim.project.elements.get(id) as any)?.category === 'Point Cloud')
            ?? [...bim.project.elements.values()].map((e: any) => (e.category === 'Point Cloud' ? e.id : null)).find(Boolean) as string | undefined;
        if (!cloudId) return 'POINTCLOUDCROP: no Point Cloud element in the project — attach a scan first.';
        runInteraction({
            steps: [PT('POINTCLOUDCROP: boundary corner 1'), PT('POINTCLOUDCROP: boundary corner 2')],
            complete: (pts) => {
                const cropMin: [number, number] = [Math.min(pts[0].x, pts[1].x), Math.min(pts[0].y, pts[1].y)];
                const cropMax: [number, number] = [Math.max(pts[0].x, pts[1].x), Math.max(pts[0].y, pts[1].y)];
                const el = useBIMStore.getState().project.elements.get(cloudId) as any;
                // LinkRenderer filters the vertex buffer by these parameters.
                useBIMStore.getState().updateElement(cloudId, {
                    parameters: { ...(el.parameters ?? {}), 'Crop Min': cropMin, 'Crop Max': cropMax },
                } as never);
                return 'POINTCLOUDCROP: scan cropped to the window (points outside hide).';
            },
        });
        return 'POINTCLOUDCROP: specify the crop window corners';
    },

    // ---------------- data & parameters ----------------
    'data.extract': () => {
        const { entities } = useAppStore.getState();
        const rows: Record<string, unknown>[] = Object.values(entities).map((e) => ({
            id: e.id, type: e.type, name: e.name, layer: e.layer, visible: e.visible,
            length: e.type === 'line' && e.data.start && e.data.end
                ? Number(Math.hypot(
                    (e.data.end[0] as number) - (e.data.start[0] as number),
                    (e.data.end[1] as number) - (e.data.start[1] as number)
                ).toFixed(4)) : null,
        }));
        const csv = ['id,type,name,layer,visible,length', ...rows.map((r) =>
            Object.values(r).map((v) => String(v ?? '')).join(',')
        )];
        downloadTextFile('data-extract.csv', csv.join('\n'), 'text/csv');
        return `DATAEXTRACTION: ${rows.length} rows written to data-extract.csv.`;
    },

    // TABLE: place a real in-drawing table entity. Rows/columns from a
    // CSV file when picked (via DATAEXTRACT-style file dialog) or a blank
    // grid sized by the prompts. The entity renders through EntityLayer.
    'data.table': (args) => {
        // A CSV file arg (DATALINK name or path) fills the grid; otherwise
        // ask for size + insertion point and create an empty table.
        const linkName = args?.[0]?.trim();
        const link = linkName ? useDataLinkStore.getState().get(linkName) : undefined;
        if (link && link.rows.length > 0) {
            runInteraction({
                steps: [PT('TABLE: insertion point')],
                complete: (pts) => {
                    const p = pts[0];
                    const cells = normalizeTableCells(link.rows);
                    undoableAdd([makeEntity('table', `Table-${Date.now()}`, {
                        cells, position: [p.x, p.y, p.z],
                        colWidth: 2, rowHeight: 0.6, hasHeader: true, linkName,
                    })]);
                    return `TABLE: inserted ${cells.length}x${cells[0].length} grid from link "${linkName}".`;
                },
            });
            return `TABLE: link "${linkName}" ready — specify insertion point`;
        }
        runInteraction({
            steps: [
                PN('TABLE: number of columns <3>'),
                PN('TABLE: number of rows (incl. header) <4>'),
                PT('TABLE: insertion point'),
            ],
            complete: (pts, values) => {
                const cols = Math.max(1, Math.round(Number(values[0]) || 3));
                const rows = Math.max(1, Math.round(Number(values[1]) || 4));
                const p = pts[0];
                const cells: string[][] = Array.from({ length: rows }, (_, r) =>
                    Array.from({ length: cols }, (_, c) => (r === 0 ? `Col ${c + 1}` : '')));
                undoableAdd([makeEntity('table', `Table-${Date.now()}`, {
                    cells, position: [p.x, p.y, p.z],
                    colWidth: 2, rowHeight: 0.6, hasHeader: true,
                })]);
                return `TABLE: ${rows}x${cols} table placed (double-click a cell to edit).`;
            },
        });
        return 'TABLE: specify columns';
    },

    'data.link': (args) => {
        // DATALINK: register a live CSV dataset under a name; TABLE <name>
        // and TABLEUPDATE pull from here. Persisted (settings slice).
        const name = args?.[0]?.trim() || `link-${Date.now()}`;
        const doRegister = (fileName: string, text: string) => {
            const rows = parseCsv(text);
            if (rows.length === 0) { notify(i18n.t('tools.notify.datalinkNoRows', { name: name }), 'warning'); return; }
            useDataLinkStore.getState().set(name, text, fileName);
            notify(i18n.t('tools.notify.datalinkBound', { name: name, fileName: fileName, count: rows.length }), 'success');
        };
        void pickAndRead('DATALINK', doRegister);
        return `DATALINK "${name}": choose the CSV file to bind.`;
    },

    'par.manager': (args) => {
        const action = (args?.[0] ?? 'list').toLowerCase();
        const mgr = globalParameterManager;
        if (action === 'list') {
            const all = mgr.getAll?.() ?? (mgr as unknown as { parameters?: Record<string, unknown> }).parameters;
            return all ? `PARAMETERS: ${Object.keys(all).join(', ') || '(none)'}` : 'PARAMETERS: (none defined)';
        }
        const [name = `Param${Date.now() % 1000}`, value = '0'] = args?.slice(1) ?? [];
        (mgr as unknown as { setParameter?: (n: string, v: unknown) => void }).setParameter?.(name, Number(value));
        return `PARAMETERS: "${name}" = ${value}.`;
    },
    'par.dim': (args) => {
        const list = requireSelection('DIMCONSTRAINT');
        if (!list) return 'DIMCONSTRAINT: select geometry first.';
        const name = args?.[0] ?? `Dim${Date.now() % 10000}`;
        const value = Number(args?.[1]) || 1;
        undoableModify(list.map((e) => e.id), (d) => ({ ...d, constraints: [...((d.constraints as string[]) ?? []), `${name}=${value}`] }));
        (globalParameterManager as unknown as { setParameter?: (n: string, v: unknown) => void }).setParameter?.(name, value);
        return `DIMCONSTRAINT: "${name}" = ${value} bound to ${list.length} object(s).`;
    },
    'par.geom': (args) => {
        const kind = (args?.[0] ?? 'coincident').toLowerCase();
        const list = requireSelection('GEOMCONSTRAINT');
        if (!list) return 'GEOMCONSTRAINT: select two objects first.';
        undoableModify(list.map((e) => e.id), (d) => ({ ...d, constraints: [...((d.constraints as string[]) ?? []), kind] }));
        return `GEOMCONSTRAINT: ${kind} applied to ${list.length} object(s).`;
    },

    // ---------------- automation / system ----------------
    'sys.script': () => {
        runInteraction({
            steps: [PTxt('SCRIPT: command lines separated by ";"')],
            complete: (_p, values) => {
                const lines = String(values[0] ?? '').split(';').map((s) => s.trim()).filter(Boolean);
                // Queue through the shared draft-command executor (lazy import
                // keeps the dynamic-input engine out of the boot graph), so
                // SCRIPT replays identically in every workbench — the old
                // 'yap:run-command' event only had an 2D-mode listener
                // and vanished into the void elsewhere.
                lines.forEach((line, i) => {
                    window.setTimeout(() => {
                        import('../../dynamicInput/dynamicInputEngine')
                            .then(({ executeDraftCommand }) => executeDraftCommand(line))
                            .catch(() => notify(i18n.t('tools.notify.scriptLineFailed', { line: line }), 'error'));
                    }, i * 50);
                });
                return `SCRIPT: ${lines.length} command(s) queued.`;
            },
        });
        return 'SCRIPT: enter commands';
    },
    'sys.macroRecord': (args) => {
        const macro = useMacroStore.getState();
        if ((args?.[0] ?? '').toUpperCase() === 'STOP' || (args?.[0] ?? '').toUpperCase() === 'ACTSTOP') {
            const steps = macro.stopAndName('macro');
            notify(i18n.t('tools.notify.actstopDone', { count: steps.length }), 'success');
            return `ACTSTOP: ${steps.length} steps captured.`;
        }
        if (macro.recording) return 'ACTRECORD: already recording (ACTSTOP to finish).';
        macro.start();
        notify(i18n.t('tools.notify.actrecordRecordingCommandsRunActstopTo'), 'info');
        return 'ACTRECORD: started. Use ACTSTOP to stop.';
    },
    'sys.historyToggle': () => {
        const s = useViewStore.getState();
        const next = !s.recordHistory;
        s.set('recordHistory', next);
        return `Record History: ${next ? 'ON — dependent geometry recomputes when its sources change.' : 'OFF — new command outputs will not record provenance.'}.`;
    },
    'sys.appLoad': () => {
        useAppStore.getState().openManageDialog('appLoad');
        return 'APPLOAD: Load Application dialog opened.';
    },
    'sys.reinit': () => {
        const pgp = useAcadPgpStore.getState();
        if (pgp.entries.length === 0 && pgp.extras.length === 0) {
            return 'REINIT: no imported acad.pgp to re-apply. Import one via ALIAS → Import acad.pgp.';
        }
        const summary = reapplyAcadPgp();
        return `REINIT: re-applied ${summary.presetAliases} alias(es) across ${summary.presetCommands} command(s) from ${pgp.fileName ?? 'acad.pgp'}.`;
    },
    'sys.standards': () => {
        const { layers } = useAppStore.getState();
        const bad = Object.values(layers).filter((l) => !l.color || !l.name);
        return bad.length === 0
            ? `CHECKSTANDARDS: ${Object.keys(layers).length} layer(s) conform.`
            : `CHECKSTANDARDS: ${bad.length} non-conforming layer(s) found.`;
    },
    'sys.compare': () => {
        downloadTextFile(`snapshot-${Date.now()}.json`, JSON.stringify(Object.values(useDocumentStore.getState().sceneObjects), null, 2));
        notify(i18n.t('tools.notify.dwgcompareCurrentSnapshotExportedCompareWith'), 'info');
        return 'DWGCOMPARE: snapshot exported for comparison.';
    },
    'sys.visualScripting': () => {
        notify(i18n.t('tools.notify.visualScriptingFrontEnd'), 'info');
        return 'VISUAL SCRIPTING: deferred by plan (docs/plans/3d/3d-full-parity-plan.md R7); the geometry kernel exists but the node editor does not.';
    },
};

/** Format import via the file picker: decode what we can, report honestly otherwise. */
function importFormat(label: string, ext: string): string {
    void (async () => {
        await pickAndRead(label, (name, text) => {
            if (ext === 'dgn' && (text.includes('Dgn') || name.endsWith('.dgn'))) {
                // Minimal DGN element scan is not implemented locally; DXF is the supported 2D path.
                notify(i18n.t('tools.notify.dgnOffline', { label: label }), 'info');
                return;
            }
            if (ext === 'fbx' && name.endsWith('.fbx')) {
                notify(i18n.t('tools.notify.fbxOffline', { label: label }), 'warning');
                return;
            }
            if (ext === 'dwf' && name.endsWith('.dwf')) {
                notify(i18n.t('tools.notify.dwfOffline', { label: label }), 'info');
                return;
            }
            // .yap/.yate/JSON → route through the document opener for real.
            const parsed = parseDocument(text);
            if (parsed) {
                notify(i18n.t('tools.notify.docRecognized', { label: label, name: name }), 'info');
                return;
            }
            notify(i18n.t('tools.notify.docUnsupported', { label: label, name: name }), 'error');
        });
    })();
    return `${label}: choose the file in the dialog…`;
}
